import { useEffect, useRef, useState } from "react"
import { DateRangePicker } from "src/shared/components/DatePicker/DateRangePicker"
import {
  DATE_FILTER_OPTIONS,
  type DateFilterKey,
  type DateRange,
  resolveDateFilter,
} from "../dateFilter"

/*
    The Monitor page's Date Filter (MPN-1530 §1.2).

    Owns the preset, not the range. The parent holds the resolved [start, end] and
    refetches on it, so the whole page moves together: Performance Snapshot, every
    chart in every tab, one request.

    Custom opens the picker and applies nothing until Apply is pressed. Re-rendering
    the page on every calendar click would fire a request per click, and the first
    click of a range is a start date with no end -- a half-picked range is not a
    question worth asking the server.
*/

type Props = {
  value: DateFilterKey
  range: DateRange
  campaignStart: Date | null
  onChange: (key: DateFilterKey, range: DateRange) => void
}

export default function MonitorDateFilter({ value, range, campaignStart, onChange }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [draft, setDraft] = useState<DateRange>(range)
  const boxRef = useRef<HTMLDivElement>(null)

  // Reopening the picker should start from what is on screen, not from whatever
  // was half-picked and abandoned last time.
  useEffect(() => {
    if (pickerOpen) setDraft(range)
  }, [pickerOpen])

  useEffect(() => {
    if (!pickerOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false)
    }
    document.addEventListener("mousedown", onDocClick)
    document.addEventListener("keydown", onEsc)
    return () => {
      document.removeEventListener("mousedown", onDocClick)
      document.removeEventListener("keydown", onEsc)
    }
  }, [pickerOpen])

  const handleSelect = (key: DateFilterKey) => {
    if (key === "custom") {
      setPickerOpen(true)
      return
    }
    setPickerOpen(false)
    onChange(key, resolveDateFilter(key, { campaignStart }))
  }

  const applyCustom = () => {
    if (!draft.start || !draft.end) return
    setPickerOpen(false)
    onChange("custom", draft)
  }

  return (
    <div className="mon-datefilter" ref={boxRef}>
      <label className="mon-datefilter__label" htmlFor="mon-datefilter-select">
        Date Filter
      </label>
      <select
        id="mon-datefilter-select"
        className="mon-datefilter__select"
        value={value}
        onChange={(e) => handleSelect(e.target.value as DateFilterKey)}
      >
        {DATE_FILTER_OPTIONS.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>

      {pickerOpen && (
        <div className="mon-datefilter__pop" role="dialog" aria-label="Custom date range">
          <DateRangePicker
            start={draft.start}
            end={draft.end}
            onChange={setDraft}
            maxDate={new Date()}
          />
          <div className="mon-datefilter__pop-actions">
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setPickerOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={applyCustom}
              disabled={!draft.start || !draft.end}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
